import type { JSX, SVGProps } from 'react';

/**
 * 界面图标。
 *
 * 三条规矩，都是为了"图标在地面站里不出错"：
 *
 * 1. **只画描边，不画填充**（`stroke` + `fill="none"`）：实心图标块在小尺寸下会糊成
 *    一个色斑，而工具栏上的图标只有 16–18px。
 * 2. **一律 `aria-hidden`**：图标旁边永远有一句中文，读屏器该念的是那句话。
 *    直接给 `<svg>` 加 `aria-label` 会让同一个控件被念两遍。
 * 3. **尺寸由 `size` 给，不靠 `className` 的 `w-4`**：图标尺寸是"这个图标多大"，
 *    属于图标本身；用工具类覆盖会让同一个图标在两处大小不同却没人知道为什么。
 *
 * 不引图标库：这里只需要寥寥几个形状，一个依赖换来的是一份要跟着升级的资源。
 */
export interface IconProps {
  /** 正方形边长（px）。默认 18 —— 与 `h-chrome` 的工具栏搭配 */
  readonly size?: number;
  readonly className?: string;
}

function Svg({
  size = 18,
  className,
  children,
}: IconProps & { readonly children: JSX.Element | readonly JSX.Element[] }): JSX.Element {
  const base: SVGProps<SVGSVGElement> = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    ...(className === undefined ? {} : { className }),
  };
  return <svg {...base}>{children}</svg>;
}

/* ------------------------------------------------------------------ 分区 */

/** 飞行：飞机。俯视对称形状 —— 一眼可辨，且不像"发送"那支纸飞机 */
export function FlyIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3.5 13.6 9.2 20.5 12l-6.9 2.8L12 20.5l-1.6-5.7L3.5 12l6.9-2.8Z" />
    </Svg>
  );
}

/** 计划：折线路径 + 两个航点 */
export function PlanIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M5 18.5c0-6 4-6 7-9s5-3.5 7-6" />
      <circle cx="5" cy="19" r="1.8" />
      <circle cx="19" cy="4" r="1.8" />
    </Svg>
  );
}

/** 设置：滑杆（不是齿轮 —— 齿轮在小尺寸下只剩一个圆点） */
export function SetupIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h9M17 17h3" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="15" cy="17" r="2" />
    </Svg>
  );
}

/** 分析：折线图 */
export function AnalyzeIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M7 15.5 11 11l3 3 4.5-5.5" />
    </Svg>
  );
}

/* ------------------------------------------------------------ 顶部状态区 */

/** 链路：插头/连接。未连接时用 `LinkOffIcon` 斜杠变体区分 */
export function LinkIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 15 15 9" />
      <path d="M11 6.5 13 4.5a3.5 3.5 0 0 1 5 5l-2 2" />
      <path d="M13 17.5 11 19.5a3.5 3.5 0 0 1-5-5l2-2" />
    </Svg>
  );
}

export function LinkOffIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 15 15 9" />
      <path d="M11 6.5 13 4.5a3.5 3.5 0 0 1 5 5l-2 2" />
      <path d="M13 17.5 11 19.5a3.5 3.5 0 0 1-5-5l2-2" />
      <path d="M3 3l18 18" />
    </Svg>
  );
}

/** 机体：四旋翼俯视 —— 顶部选择器用它标示"这一栏选的是哪台" */
export function VehicleIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="9.5" y="9.5" width="5" height="5" rx="1.2" />
      <path d="M9.5 9.5 6 6M14.5 9.5 18 6M9.5 14.5 6 18M14.5 14.5 18 18" />
      <circle cx="5" cy="5" r="1.6" />
      <circle cx="19" cy="5" r="1.6" />
      <circle cx="5" cy="19" r="1.6" />
      <circle cx="19" cy="19" r="1.6" />
    </Svg>
  );
}

/** 定位到机体（地图上的"回到飞机"） */
export function LocateIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="6.5" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      <circle cx="12" cy="12" r="1.6" />
    </Svg>
  );
}

/** 跟随 / 全览：四角框 */
export function FrameIcon(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15" />
    </Svg>
  );
}
